/**
 * Web implementation of notifications page service
 * Uses Supabase for authenticated users with localStorage fallback
 */

import type {
  INotificationsPageService,
  NotificationType,
  NotificationSection,
  NotificationPreferences,
  NotificationUpdateResult
} from '../../contracts'
import { getSupabaseClient, useAuthStore } from '@restaurant/core'
import { errorLogFields, safeGetLogger } from '@restaurant/logging'


/**
 * Database row type for user_notification_preferences table
 */
interface UserNotificationPreferencesRow {
  id: string
  user_id: string
  section_preferences: Record<string, NotificationType[]>
  global_enabled: boolean
  user_consent: boolean
  created_at: string
  updated_at: string
}

/**
 * Storage keys for notification preferences
 */
const STORAGE_KEYS = {
  NOTIFICATION_PREFERENCES: 'restaurant_notification_preferences',
  USER_CONSENT: 'restaurant_notification_consent',
  LAST_UPDATED: 'restaurant_notification_last_updated'
} as const

/**
 * Default notification sections matching PageContentNotifications component
 */
const DEFAULT_SECTIONS: NotificationSection[] = [
  {
    id: 'order_updates',
    name: 'Order Updates',
    description: 'Stay informed about your order progress and delivery updates',
    selectedTypes: ['Push', 'Text'], // Default to Push and Text selected
    enabled: true
  },
  {
    id: 'restaurant_updates',
    name: 'Restaurant Updates',
    description: 'Get notified about exclusive deals, promotions, and loyalty rewards',
    selectedTypes: ['Push'], // Default to only Push selected
    enabled: true
  }
]

/**
 * Web-specific notifications page service implementation
 * Uses Supabase for authenticated users with localStorage as cache/fallback
 */
export class WebNotificationsPageService implements INotificationsPageService {

  /**
   * Check if user is currently authenticated
   */
  private isUserAuthenticated(): { isAuthenticated: boolean; userId: string | null } {
    const authState = useAuthStore.getState()
    return {
      isAuthenticated: authState.isAuthenticated && !!authState.user,
      userId: authState.user?.id ?? null
    }
  }

  /**
   * Load preferences from Supabase
   */
  private async loadFromSupabase(userId: string): Promise<NotificationPreferences | null> {
    try {
      const supabase = getSupabaseClient()
      const { data, error } = await supabase
        .from('user_notification_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle()

      if (error) {
        const logger = safeGetLogger()
        logger?.error('Failed to load preferences from Supabase', { domain: 'notifications', ...errorLogFields(error) })
        return null
      }

      if (!data) {
        return null // No preferences stored yet
      }

      const row = data as UserNotificationPreferencesRow
      return {
        sections: row.section_preferences,
        globalEnabled: row.global_enabled,
        userConsent: row.user_consent,
        lastUpdated: new Date(row.updated_at)
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Supabase load error', { domain: 'notifications', ...errorLogFields(error) })
      return null
    }
  }

  /**
   * Save preferences to Supabase using upsert
   */
  private async saveToSupabase(
    userId: string,
    preferences: NotificationPreferences
  ): Promise<boolean> {
    try {
      const logger = safeGetLogger()
      logger?.notifications('preference_updated', { userId, sections: JSON.stringify(preferences.sections) })

      const supabase = getSupabaseClient()
      const { error } = await supabase
        .from('user_notification_preferences')
        .upsert({
          user_id: userId,
          section_preferences: preferences.sections,
          global_enabled: preferences.globalEnabled,
          user_consent: preferences.userConsent
        }, {
          onConflict: 'user_id'
        })

      if (error) {
        const logger = safeGetLogger()
        logger?.error('Supabase save failed', { domain: 'notifications', ...errorLogFields(error) })
        return false
      }

      const logger2 = safeGetLogger()
      logger2?.notifications('loaded', { source: 'supabase', success: true })
      return true
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Supabase save error', { domain: 'notifications', ...errorLogFields(error) })
      return false
    }
  }

  /**
   * Load preferences from localStorage
   */
  private loadFromLocalStorage(): NotificationPreferences | null {
    try {
      const storedPreferences = localStorage.getItem(STORAGE_KEYS.NOTIFICATION_PREFERENCES)
      const storedConsent = localStorage.getItem(STORAGE_KEYS.USER_CONSENT)
      const storedLastUpdated = localStorage.getItem(STORAGE_KEYS.LAST_UPDATED)

      if (storedPreferences && storedConsent) {
        const preferences: NotificationPreferences = {
          sections: JSON.parse(storedPreferences),
          globalEnabled: JSON.parse(storedConsent),
          lastUpdated: storedLastUpdated ? new Date(storedLastUpdated) : new Date(),
          userConsent: JSON.parse(storedConsent)
        }

        if (this.validatePreferences(preferences)) {
          return preferences
        }
      }
      return null
    } catch (error) {
      const logger = safeGetLogger()
      logger?.warn('Failed to load from localStorage', { domain: 'notifications' })
      return null
    }
  }

  /**
   * Save preferences to localStorage (cache/fallback)
   */
  private saveToLocalStorage(preferences: NotificationPreferences): void {
    try {
      localStorage.setItem(
        STORAGE_KEYS.NOTIFICATION_PREFERENCES,
        JSON.stringify(preferences.sections)
      )
      localStorage.setItem(
        STORAGE_KEYS.USER_CONSENT,
        JSON.stringify(preferences.globalEnabled)
      )
      localStorage.setItem(
        STORAGE_KEYS.LAST_UPDATED,
        preferences.lastUpdated.toISOString()
      )
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to save to localStorage', { domain: 'notifications', ...errorLogFields(error) })
    }
  }

  /**
   * Migrate localStorage data to Supabase for newly authenticated user
   */
  private async migrateLocalStorageToSupabase(userId: string): Promise<void> {
    try {
      const localPrefs = this.loadFromLocalStorage()
      if (!localPrefs) return

      // Check if user already has preferences in Supabase
      const supabasePrefs = await this.loadFromSupabase(userId)

      if (!supabasePrefs) {
        // No Supabase data - migrate localStorage data
        await this.saveToSupabase(userId, localPrefs)
        const logger = safeGetLogger()
        logger?.notifications('preference_updated', { action: 'migrated_to_supabase' })
      } else if (localPrefs.lastUpdated > supabasePrefs.lastUpdated) {
        // Local data is newer - sync to Supabase
        await this.saveToSupabase(userId, localPrefs)
        const logger = safeGetLogger()
        logger?.notifications('preference_updated', { action: 'synced_newer_local_to_supabase' })
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Migration error', { domain: 'notifications', ...errorLogFields(error) })
    }
  }

  /**
   * Load current notification preferences
   * Uses Supabase for authenticated users, localStorage for others
   */
  async loadNotificationPreferences(): Promise<NotificationPreferences> {
    const { isAuthenticated, userId } = this.isUserAuthenticated()

    if (isAuthenticated && userId) {
      // Authenticated user: Try Supabase first
      try {
        // Attempt migration if there's localStorage data
        await this.migrateLocalStorageToSupabase(userId)

        const supabasePrefs = await this.loadFromSupabase(userId)
        if (supabasePrefs && this.validatePreferences(supabasePrefs)) {
          // Update localStorage cache
          this.saveToLocalStorage(supabasePrefs)
          return supabasePrefs
        }
      } catch (error) {
        const logger = safeGetLogger()
        logger?.warn('Supabase unavailable, falling back to localStorage', { domain: 'notifications' })
      }
    }

    // Fallback: localStorage (unauthenticated or offline)
    const localPrefs = this.loadFromLocalStorage()
    if (localPrefs) {
      return localPrefs
    }

    // Return defaults if nothing found
    return this.getDefaultPreferences()
  }

  /**
   * Get default notification sections
   */
  async getDefaultSections(): Promise<NotificationSection[]> {
    return [...DEFAULT_SECTIONS]
  }

  /**
   * Update notification preferences for a specific section
   */
  async updateSectionPreferences(
    sectionId: string,
    selectedTypes: NotificationType[]
  ): Promise<NotificationUpdateResult> {
    try {
      const currentPreferences = await this.loadNotificationPreferences()

      // Update the specific section
      const updatedSections = {
        ...currentPreferences.sections,
        [sectionId]: selectedTypes
      }

      const updatedPreferences: NotificationPreferences = {
        ...currentPreferences,
        sections: updatedSections,
        lastUpdated: new Date()
      }

      // Validate before saving
      if (!this.validatePreferences(updatedPreferences)) {
        return {
          success: false,
          error: 'Invalid notification preferences'
        }
      }

      // Save to localStorage
      await this.savePreferences(updatedPreferences)

      return {
        success: true,
        preferences: updatedPreferences
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to update section preferences', { domain: 'notifications', ...errorLogFields(error) })
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Update multiple sections at once
   */
  async updateAllPreferences(
    preferences: Record<string, NotificationType[]>
  ): Promise<NotificationUpdateResult> {
    try {
      const logger = safeGetLogger()
      logger?.notifications('preference_updated', { sections: JSON.stringify(preferences) })

      const currentPreferences = await this.loadNotificationPreferences()

      const updatedPreferences: NotificationPreferences = {
        ...currentPreferences,
        sections: preferences,
        lastUpdated: new Date()
      }

      // Validate before saving
      if (!this.validatePreferences(updatedPreferences)) {
        const logger2 = safeGetLogger()
        logger2?.error('Validation failed for notification preferences', { domain: 'notifications', error: 'invalid_preferences' })
        return {
          success: false,
          error: 'Invalid notification preferences'
        }
      }

      // Save to localStorage and Supabase
      await this.savePreferences(updatedPreferences)

      const logger3 = safeGetLogger()
      logger3?.notifications('loaded', { source: 'updateAllPreferences', success: true })
      return {
        success: true,
        preferences: updatedPreferences
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to update all preferences', { domain: 'notifications', ...errorLogFields(error) })
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Enable or disable global notifications
   */
  async updateGlobalSettings(enabled: boolean): Promise<NotificationUpdateResult> {
    try {
      const currentPreferences = await this.loadNotificationPreferences()

      const updatedPreferences: NotificationPreferences = {
        ...currentPreferences,
        globalEnabled: enabled,
        userConsent: enabled,
        lastUpdated: new Date()
      }

      // If disabling globally, request permissions for future use
      let permissionRequested = false
      if (enabled) {
        permissionRequested = await this.requestNotificationPermissions()
      }

      // Save to localStorage
      await this.savePreferences(updatedPreferences)

      return {
        success: true,
        preferences: updatedPreferences,
        permissionRequested
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to update global settings', { domain: 'notifications', ...errorLogFields(error) })
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Request device notification permissions (browser)
   */
  async requestNotificationPermissions(): Promise<boolean> {
    try {
      // Check if browser supports notifications
      if (!('Notification' in window)) {
        const logger = safeGetLogger()
        logger?.warn('Browser does not support notifications', { domain: 'notifications' })
        return false
      }

      // Check current permission status
      if (Notification.permission === 'granted') {
        return true
      }

      if (Notification.permission === 'denied') {
        return false
      }

      // Request permission
      const permission = await Notification.requestPermission()
      return permission === 'granted'
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to request notification permissions', { domain: 'notifications', ...errorLogFields(error) })
      return false
    }
  }

  /**
   * Check current notification permissions
   */
  async checkNotificationPermissions(): Promise<boolean> {
    try {
      if (!('Notification' in window)) {
        return false
      }

      return Notification.permission === 'granted'
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to check notification permissions', { domain: 'notifications', ...errorLogFields(error) })
      return false
    }
  }

  /**
   * Clear all notification preferences (reset to defaults)
   */
  async clearPreferences(): Promise<NotificationUpdateResult> {
    try {
      // Remove all stored preferences from localStorage
      localStorage.removeItem(STORAGE_KEYS.NOTIFICATION_PREFERENCES)
      localStorage.removeItem(STORAGE_KEYS.USER_CONSENT)
      localStorage.removeItem(STORAGE_KEYS.LAST_UPDATED)

      // If authenticated, also delete from Supabase
      const { isAuthenticated, userId } = this.isUserAuthenticated()
      if (isAuthenticated && userId) {
        const supabase = getSupabaseClient()
        await supabase
          .from('user_notification_preferences')
          .delete()
          .eq('user_id', userId)
      }

      // Return default preferences
      const defaultPreferences = this.getDefaultPreferences()

      return {
        success: true,
        preferences: defaultPreferences
      }
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to clear preferences', { domain: 'notifications', ...errorLogFields(error) })
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Validate notification preferences structure
   */
  validatePreferences(preferences: NotificationPreferences): boolean {
    try {
      // Check required fields
      if (!preferences || typeof preferences !== 'object') {
        return false
      }

      if (typeof preferences.globalEnabled !== 'boolean') {
        return false
      }

      if (typeof preferences.userConsent !== 'boolean') {
        return false
      }

      if (!(preferences.lastUpdated instanceof Date)) {
        return false
      }

      if (!preferences.sections || typeof preferences.sections !== 'object') {
        return false
      }

      // Validate sections
      for (const [sectionId, types] of Object.entries(preferences.sections)) {
        if (typeof sectionId !== 'string' || !Array.isArray(types)) {
          return false
        }

        // Validate notification types
        for (const type of types) {
          if (!['Push', 'Text', 'Email'].includes(type)) {
            return false
          }
        }
      }

      return true
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Failed to validate preferences', { domain: 'notifications', ...errorLogFields(error) })
      return false
    }
  }

  /**
   * Get default notification preferences
   */
  private getDefaultPreferences(): NotificationPreferences {
    const defaultSections: Record<string, NotificationType[]> = {}
    DEFAULT_SECTIONS.forEach(section => {
      defaultSections[section.id] = section.selectedTypes
    })

    return {
      sections: defaultSections,
      globalEnabled: true,
      lastUpdated: new Date(),
      userConsent: true
    }
  }

  /**
   * Save preferences to localStorage and Supabase (if authenticated)
   */
  private async savePreferences(preferences: NotificationPreferences): Promise<void> {
    // Always save to localStorage (cache/fallback)
    this.saveToLocalStorage(preferences)

    // If authenticated, also save to Supabase
    const { isAuthenticated, userId } = this.isUserAuthenticated()
    if (isAuthenticated && userId) {
      const success = await this.saveToSupabase(userId, preferences)
      if (!success) {
        const logger = safeGetLogger()
        logger?.warn('Failed to sync preferences to Supabase - will retry on next save', { domain: 'notifications' })
      }
    }
  }
}
